<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useToast } from 'vue-toast-notification'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import '@/assets/multiselect.css'
import { useContactStore } from '@/stores/useContactStore'
import RadioInput from '@/components/common/inputs/RadioInput.vue'
import WysiwygInput from '@/components/common/inputs/WysiwygInput.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import { PlusIcon } from '@heroicons/vue/24/outline'
import { useContextualHelp } from '@/composables/useContextualHelp'
import HelpButton from '@/components/common/help/HelpButton.vue'

interface Props {
  preselectedCompany?: { uuid: string; name: string }
  lightweight?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: []
  created: [contactUuid?: string]
}>()

const $toast = useToast()
const contactStore = useContactStore()
const isInternalOpen = ref(false)
const wasValidated = ref(false)

// Aide contextuelle (USER_MASTER) — active uniquement quand la modale est
// ouverte ; surcharge l'aide de la page puis la restaure à la fermeture.
useContextualHelp('contact-create', isInternalOpen)

const titles = [
  { id: '1', value: 'Monsieur', label: 'Monsieur' },
  { id: '2', value: 'Madame', label: 'Madame' }
]

const mappedTitles = computed(() => titles)

const loading = ref(false)
const companySearchLoading = ref(false)
const companyOptions = ref<any[]>([])

const form = reactive({
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  mobile: '',
  function: '',
  company: null as any,
  title: undefined as string | undefined,
  isSigner: false,
  comment: '',
  portalClient: false,
  accessBilling: false,
  accessTickets: false,
  accessAdminContact: false,
  accessReadContact: false
})

watch(() => props.preselectedCompany, (newVal) => {
  if (newVal) {
    form.company = newVal
  }
}, { immediate: true })

const fieldErrors = computed(() => {
  const errors: Record<string, string> = {}
  if (!form.title) {
    errors.title = 'Veuillez sélectionner une civilité'
  }
  if (!form.firstname.trim()) {
    errors.firstname = 'Prénom requis'
  }
  if (!form.lastname.trim()) {
    errors.lastname = 'Nom requis'
  }
  if (!form.email.trim()) {
    errors.email = 'Email requis'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = 'Email format invalide'
  }
  if (!form.company?.uuid) {
    errors.company = 'Veuillez sélectionner une société'
  }
  return errors
})

const isFormValid = computed(() => {
  return Object.keys(fieldErrors.value).length === 0
})

function handleOpen() {
  if (props.preselectedCompany) {
    form.company = props.preselectedCompany
  }
  isInternalOpen.value = true
}

function handleClose() {
  resetForm()
  isInternalOpen.value = false
  emit('close')
}

function resetForm() {
  wasValidated.value = false
  form.firstname = ''
  form.lastname = ''
  form.email = ''
  form.phone = ''
  form.mobile = ''
  form.function = ''
  form.company = null
  form.title = undefined
  form.isSigner = false
  form.comment = ''
  form.portalClient = false
  form.accessBilling = false
  form.accessTickets = false
  form.accessAdminContact = false
  form.accessReadContact = false
  companyOptions.value = []
}

async function onSearchCompany(query: string) {
  if (query.length < 3) {
    companyOptions.value = []
    return
  }
  companySearchLoading.value = true
  try {
    const results = await contactStore.searchCompanies(query)
    companyOptions.value = Array.isArray(results.data) ? results.data : []
  } finally {
    companySearchLoading.value = false
  }
}

async function handleCreate() {
  wasValidated.value = true
  if (!isFormValid.value) return
  if (!form.company?.uuid) {
    $toast.error('Veuillez sélectionner une société.')
    return
  }

  loading.value = true
  try {
    const portalPermissions: string[] = []
    if (form.portalClient) {
      if (form.accessBilling) portalPermissions.push('PORTAL_BILLING')
      if (form.accessTickets) portalPermissions.push('PORTAL_TICKETS')
      if (form.accessAdminContact) portalPermissions.push('PORTAL_CONTACT_ADMIN')
      if (form.accessReadContact) portalPermissions.push('PORTAL_CONTACT_READ')
    }
    const payload = {
      firstname: form.firstname,
      lastname: form.lastname,
      email: form.email,
      phone: form.phone ? `+33${form.phone.replace(/\s/g, '').replace(/^0/, '')}` : '',
      mobile: form.mobile ? `+33${form.mobile.replace(/\s/g, '').replace(/^0/, '')}` : '',
      function: form.function,
      isSigner: form.isSigner,
      comment: form.comment,
      civility: form.title as 'Monsieur' | 'Madame' | undefined,
      ...(portalPermissions.length > 0 && { portalPermissions }),
    }
    const result = await contactStore.createSuiteContact(form.company.uuid, payload)

    if (result.success) {
      emit('created', (result as any).contactUuid)
      handleClose()
    } else {
      $toast.error(result.message)
    }
  } catch (error: any) {
    $toast.error(error?.message ?? 'Erreur lors de la création du contact.')
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <ButtonDaisy
    :text="lightweight ? '' : 'Nouveau contact'"
    variant="primary"
    :icon="PlusIcon"
    @click="handleOpen"
  />

  <dialog class="modal" :class="{ 'modal-open': isInternalOpen }">
    <div class="modal-box max-w-2xl p-0 overflow-hidden">
      <div class="p-4 border-b bg-base-200/50 flex justify-between items-center">
        <div class="flex items-center gap-1">
          <h3 class="text-lg font-bold">Créer un nouveau contact</h3>
          <HelpButton />
        </div>
        <button class="btn btn-sm btn-circle btn-ghost" @click="handleClose">✕</button>
      </div>

      <div class="p-6 max-h-[80vh] overflow-y-auto">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
          <!-- Société -->
          <div v-if="!props.preselectedCompany" class="col-span-1 md:col-span-2">
            <label class="block text-xs font-bold uppercase text-base-content/60 mb-1">Société *</label>
            <Multiselect
              v-model="form.company"
              :options="companyOptions"
              :searchable="true"
              :loading="companySearchLoading"
              :internal-search="false"
              placeholder="Rechercher une société..."
              label="name"
              track-by="uuid"
              @search-change="onSearchCompany"
              :disabled="loading || !!props.preselectedCompany"
              :class="{ 'multiselect-error': wasValidated && fieldErrors.company }"
            >
              <template #noOptions>
                <div class="p-2 text-xs">Saisissez au moins 3 caractères</div>
              </template>
              <template #noResult>
                <div class="p-2 text-xs">Aucun résultat trouvé</div>
              </template>
            </Multiselect>
            <span v-if="wasValidated && fieldErrors.company" class="text-xs text-error mt-1">{{ fieldErrors.company }}</span>
          </div>

          <!-- Civilité -->
          <div class="col-span-1 md:col-span-2">
            <RadioInput
              label="Civilité"
              :options="mappedTitles || []"
              v-model="form.title"
              required
              itemClasses="flex-wrap"
              labelClasses="block text-xs font-bold uppercase text-base-content/60 mb-1"
              :disabled="loading"
              :error-message="wasValidated ? fieldErrors.title : undefined"
            />
          </div>

          <!-- Prénom -->
          <div>
            <label class="block text-xs font-bold uppercase text-base-content/60 mb-1">Prénom *</label>
            <input v-model="form.firstname" type="text" placeholder="Prénom"
                   class="input input-bordered input-sm w-full" :class="{ 'input-error': wasValidated && fieldErrors.firstname }" :disabled="loading" required />
            <span v-if="wasValidated && fieldErrors.firstname" class="text-xs text-error mt-1">{{ fieldErrors.firstname }}</span>
          </div>

          <!-- Nom -->
          <div>
            <label class="block text-xs font-bold uppercase text-base-content/60 mb-1">Nom *</label>
            <input v-model="form.lastname" type="text" placeholder="Nom" class="input input-bordered input-sm w-full"
                   :class="{ 'input-error': wasValidated && fieldErrors.lastname }"
                   :disabled="loading" required />
            <span v-if="wasValidated && fieldErrors.lastname" class="text-xs text-error mt-1">{{ fieldErrors.lastname }}</span>
          </div>

          <!-- Email -->
          <div class="col-span-1 md:col-span-2">
            <label class="block text-xs font-bold uppercase text-base-content/60 mb-1">Email *</label>
            <input v-model="form.email" type="email" placeholder="email@exemple.com"
                   class="input input-bordered input-sm w-full" :class="{ 'input-error': wasValidated && fieldErrors.email }" :disabled="loading" required />
            <span v-if="wasValidated && fieldErrors.email" class="text-xs text-error mt-1">{{ fieldErrors.email }}</span>
          </div>

          <template v-if="!props.lightweight">
            <!-- Téléphone fixe -->
            <div>
              <label class="block text-xs font-bold uppercase text-base-content/60 mb-1">Téléphone fixe</label>
              <div class="join w-full">
              <span
                class="btn btn-sm join-item no-animation bg-base-200 pointer-events-none">+33</span>
                <input v-model="form.phone" type="tel" placeholder="01 00 00 00 00"
                       class="input input-bordered input-sm join-item w-full" :disabled="loading" />
              </div>
            </div>

            <!-- Mobile -->
            <div>
              <label class="block text-xs font-bold uppercase text-base-content/60 mb-1">Mobile</label>
              <div class="join w-full">
              <span
                class="btn btn-sm join-item no-animation bg-base-200 pointer-events-none">+33</span>
                <input v-model="form.mobile" type="tel" placeholder="06 00 00 00 00"
                       class="input input-bordered input-sm join-item w-full" :disabled="loading" />
              </div>
            </div>

            <!-- Fonction -->
            <div>
              <label class="block text-xs font-bold uppercase text-base-content/60 mb-1">Fonction</label>
              <input v-model="form.function" type="text" placeholder="Ex: Responsable Achats"
                     class="input input-bordered input-sm w-full" :disabled="loading" />
            </div>

            <!-- Signataire -->
            <div class="col-span-1 md:col-span-2">
              <div class="flex items-center gap-4">
                <label class="text-xs font-bold uppercase text-base-content/60">Signataire</label>
                <div class="flex items-center">
                  <input v-model="form.isSigner" type="checkbox" class="toggle toggle-primary toggle-sm"
                         :disabled="loading" />
                  <span class="ml-2 text-sm">{{ form.isSigner ? 'Oui' : 'Non' }}</span>
                </div>
              </div>
            </div>

            <!-- Portail Client -->
            <div class="col-span-1 md:col-span-2 border-t mt-2 pt-4">
              <div class="flex items-center">
                <input v-model="form.portalClient" type="checkbox" class="checkbox checkbox-primary checkbox-sm"
                       :disabled="loading" />
                <span class="ml-2 text-sm font-bold uppercase text-base-content/60">Portail Client</span>
              </div>

              <div v-if="form.portalClient" class="grid grid-cols-2 gap-4 mt-3 ml-6 p-3 bg-base-200/30 rounded-lg">
                <div class="flex items-center">
                  <input v-model="form.accessBilling" type="checkbox" class="checkbox checkbox-xs checkbox-primary"
                         :disabled="loading" />
                  <span class="ml-2 text-xs">Accès Facturation</span>
                </div>
                <div class="flex items-center">
                  <input v-model="form.accessTickets" type="checkbox" class="checkbox checkbox-xs checkbox-primary"
                         :disabled="loading" />
                  <span class="ml-2 text-xs">Accès Tickets</span>
                </div>
                <div class="flex items-center">
                  <input v-model="form.accessAdminContact" type="checkbox" class="checkbox checkbox-xs checkbox-primary"
                         :disabled="loading" />
                  <span class="ml-2 text-xs">Accès Admin Contacts</span>
                </div>
                <div class="flex items-center">
                  <input v-model="form.accessReadContact" type="checkbox" class="checkbox checkbox-xs checkbox-primary"
                         :disabled="loading" />
                  <span class="ml-2 text-xs">Accès Lecture Contacts</span>
                </div>
              </div>
            </div>

            <!-- Commentaire -->
            <div class="col-span-1 md:col-span-2 border-t mt-2 pt-4">
              <WysiwygInput
                name="comment"
                label="Commentaire"
                v-model="form.comment"
                :disabled="loading"
              />
            </div>
          </template>
        </div>
      </div>

      <div class="modal-action p-4 border-t bg-base-200/50 mt-0 flex justify-between items-center">
        <div class="flex-1">
          <ul v-if="!isFormValid && wasValidated" class="text-error text-[10px] grid grid-cols-2 gap-x-2 list-disc list-inside">
            <li v-for="(error, key) in fieldErrors" :key="key">{{ error }}</li>
          </ul>
        </div>
        <div class="flex gap-2">
          <button class="btn btn-sm" @click="handleClose" :disabled="loading">Annuler</button>
          <button
            class="btn btn-sm btn-primary"
            @click="handleCreate"
            :disabled="loading"
          >
            <span v-if="loading" class="loading loading-spinner loading-xs"></span>
            Valider
          </button>
        </div>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="handleClose">Fermer</button>
    </form>
  </dialog>
</template>

<style>
/* Multiselect styling to match the project */
.multiselect__tags {
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0.5rem !important;
}

.multiselect-error .multiselect__tags {
  border-color: #ff5861;
}

</style>
